import React, { useRef, useEffect, useState } from 'react';
import cx from 'classnames';
import './UpdatePhoneView.scss';
import PropTypes from 'prop-types';
import InfoOutlineIcon from '../../../svg/icons/ic_info-outline_red_35x35.svg';
import ProcessingIcon from '../../../svg/icons/processing-spinner-accessible-red.svg';
import TranslationProvider, { getTranslation } from '../../../providers/TranslationProvider';
import Button from '../../Button';
import { trackAuthDdAction } from '../../../utils/authDatadog';
import {
  RESY_WEB_PARE_DOWN_OTP_AUTOCOMPLETE_DEFINITIONS_CON_19753,
} from '../../../constants/optimizelyExperiments';
import useExperiment from '../../Experiments/useExperiment';

const NUMBER_OF_DIGITS = 6;
const EMPTY_STATE = new Array(NUMBER_OF_DIGITS).fill('');
// Remove anything not a digit character from incoming typed values, including whitespace.
const sanitizeOtpValue = (value = '') => value.replace(/\D/g, '');
const isSingleDigit = (value = '') => /^\d$/.test(value);

const InputCode = (props) => {
  const {
    processing, onComplete, error, onResend, resendDatadogActionName, completeDatadogActionName,
  } = props;
  const [enableOnlySingleAutocompleteField] = useExperiment(RESY_WEB_PARE_DOWN_OTP_AUTOCOMPLETE_DEFINITIONS_CON_19753);
  const [otp, setOtp] = useState(EMPTY_STATE);
  const [otpError, setOtpError] = useState(error);
  // Two status slots let us alternate live region updates for repeated messages.
  // Some screen readers (notably VoiceOver) can ignore repeated text in one region.
  const [otpAutoActionStatus, setOtpAutoActionStatus] = useState(['', '']);
  const [otpAutoActionStatusIndex, setOtpAutoActionStatusIndex] = useState(0);
  const otpBoxReference = useRef([]);
  // onComplete is invoked from a deferred callback, so keep a ref to the latest one.
  const onCompleteReference = useRef(onComplete);
  onCompleteReference.current = onComplete;

  const announceAutoActionStatus = (statusMessage) => {
    if (statusMessage) {
      const nextStatusIndex = (otpAutoActionStatusIndex + 1) % 2;
      // Write to only one live region at a time, clearing the other, so each
      // announcement is treated as a fresh DOM update.
      setOtpAutoActionStatus([
        nextStatusIndex === 0 ? statusMessage : '',
        nextStatusIndex === 1 ? statusMessage : '',
      ]);
      setOtpAutoActionStatusIndex(nextStatusIndex);
    }
  };

  useEffect(() => {
    setOtpError(error);
  }, [error]);

  const onChange = (e, index) => {
    const unsanitizedValue = e.target.value;
    let value = sanitizeOtpValue(unsanitizedValue);
    if (value !== unsanitizedValue) {
      // The user entered a disallowed character, don't let that go into the (controlled) component state;
      // just do an early return.
      return;
    }
    if (value.length > 2) {
      return; // This helps to skip onChange while auto filling in mobile web
    }

    if (value.length === 2) {
      value = value.substring(1); // This rewrites the value if a box is already filled.
    }
    setOtpError(null);
    setOtp(otp.toSpliced(index, 1, value));
  };

  const handleBackspace = (e, index) => {
    if (e.key === 'Backspace' && index > 0) {
      announceAutoActionStatus(getTranslation({
        translationKey: 'otp_moved_to_previous_digit',
        fallback: 'Moved to previous digit',
      }));
      otpBoxReference.current[index - 1].focus();
    }
  };

  const onPaste = (e) => {
    const code = sanitizeOtpValue(e.clipboardData.getData('Text'));
    if (code.length === NUMBER_OF_DIGITS) {
      setOtp(code.split(''));
    } else {
      // Prevent any behavior from the paste operation if the code is not of the expected length,
      // including triggering the onInput() or handleChange()
      e.preventDefault();
    }
  };

  const onInput = (e) => {
    const value = sanitizeOtpValue(e.target.value);
    if (value.length === NUMBER_OF_DIGITS) { // Should consider this fn only when prefilling from messages
      setOtp(value.split(''));
    }
  };

  // Advancing the focus is handled entirely here. Regular onInput (and onChange) is handled
  // in the event capturing phase, earlier, not the bubbling phase.
  const onInputForFocusChange = (e, index) => {
    const typedCharacter = e.nativeEvent?.data;
    if (isSingleDigit(typedCharacter) && index < NUMBER_OF_DIGITS - 1) {
      announceAutoActionStatus(getTranslation({
        translationKey: 'otp_moved_to_next_digit',
        fallback: 'Moved to next digit',
      }));
      otpBoxReference.current[index + 1]?.focus();
    }
  };

  const resend = () => {
    setOtp(EMPTY_STATE);
    trackAuthDdAction(resendDatadogActionName, { source: 'auth' });
    onResend?.();
  };

  useEffect(() => {
    const updatedOtp = otp.join('');
    if (updatedOtp.length === NUMBER_OF_DIGITS) {
      // First, blur the current focus. If we don't do this, the <input>
      // element the user was typing in will cycle through being disabled, then re-enabled,
      // which will cause that <input> to lose and then regain focus and can prompt a
      // screenreader (VoiceOver, notably) to read the input's *value* again, stomping
      // any other messages the screenreader was just barely starting to read. Including,
      // critically, the "Oops that didn't work" alert content.
      //
      // Also, we do this here (rather than in onInputForFocusChange) so as to capture
      // paste events that fill the entire OTP at once, or autofill from messages events.
      //
      // Do this only in a double- requestAnimationFrame() callback so that
      // VoiceOver doesn't lose track of the blur having happened.
      //
      // onComplete() is invoked from that same callback, *after* the blur: callers
      // typically set processing synchronously, which disables the inputs, and we
      // need the blur to happen before that disable/re-enable cycle.
      //
      // We do datadog tracking on similar timing, though this is less critical.
      let animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = window.requestAnimationFrame(() => {
          const { activeElement } = document;
          if (activeElement && otpBoxReference.current.includes(activeElement)) {
            activeElement.blur();
          }
          trackAuthDdAction(completeDatadogActionName, { source: 'auth' });
          onCompleteReference.current(updatedOtp);
        });
      });
      const otpAsVoiceoverString = otp.join(' ');
      announceAutoActionStatus(getTranslation({
        translationKey: 'otp_complete_received_submitting',
        translationValues: { otpCode: otpAsVoiceoverString },
        fallback: `Complete OTP received. It is ${otpAsVoiceoverString}. Submitting...`,
      }));
      // If the OTP changes or we unmount before the frames fire, don't submit a stale code.
      return () => window.cancelAnimationFrame(animationFrameId);
    }
    return undefined;
  }, [otp, completeDatadogActionName]);

  const getInputField = (index, digit, invalid) => (
    <input
      key={index}
      value={digit}
      disabled={processing}
      onChangeCapture={(e) => onChange(e, index)}
      onKeyUp={(e) => handleBackspace(e, index)}
      onPaste={onPaste}
      onInputCapture={onInput}
      onInput={(e) => onInputForFocusChange(e, index)}
      ref={(reference) => { otpBoxReference.current[index] = reference; }}
      className={cx('InputCode__Field-cell', { 'InputCode__Field-cell--error': (otpError && !processing) })}
      autoComplete={!enableOnlySingleAutocompleteField || index === 0 ? 'one-time-code' : undefined}
      id={`otp-${index}`}
      inputMode="numeric"
      aria-label={getTranslation({ translationKey: 'otp_digit_number', translationValues: { digitNumber: index + 1 }, fallback: `Digit number ${index + 1} in OTP code` })}
      aria-invalid={invalid}
      autoFocus={index === 0} // eslint-disable-line jsx-a11y/no-autofocus
      pattern="[0-9]*"
    />
  );

  return (
    <div className="InputCode__Container">
      <div
        className="InputCode__Field-container"
        role="group"
        aria-label={getTranslation({ translationKey: 'otp_code_group', fallback: 'One-time passcode input' })}
        aria-describedby={otpError ? 'input_code_error' : undefined}
      >
        {otp.map((digit, index) => {
          const field = getInputField(index, digit, otpError !== null);
          return ((index) === NUMBER_OF_DIGITS / 2) ? (
            // This case puts a visual divider in the UI at its midpoint
            <>
              <hr className="InputCode__Divider" />
              {field}
            </>
          ) : field;
        })}
      </div>
      <div
        className="InputCode__Status-announcement"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {otpAutoActionStatus[0]}
      </div>
      <div
        className="InputCode__Status-announcement"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {otpAutoActionStatus[1]}
      </div>
      {processing && <ProcessingIcon data-testid="processingIcon" />}
      {otpError && (
        <div className="InputCode__Error-container">
          <InfoOutlineIcon className="InputCode__Field--info" />
          <p id="input_code_error" role="alert" className={`InputCode__Field-error ${(otpError && !processing) ? 'error-show' : ''}`}>{otpError}</p>
        </div>
      )}
      {!processing && onResend && (
      <TranslationProvider
        translationKey="resend_code"
      >
        <Button
          onClick={resend}
          type="button"
          variant="link"
          id="resend-code-button"
          data-dd-action-name={resendDatadogActionName}
        >
          Resend Code
        </Button>
      </TranslationProvider>
      )}
    </div>
  );
};

InputCode.propTypes = {
  processing: PropTypes.bool,
  error: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
  onResend: PropTypes.func,
  resendDatadogActionName: PropTypes.string,
  completeDatadogActionName: PropTypes.string,
};

InputCode.defaultProps = {
  processing: false,
  error: undefined,
  onResend: undefined,
  resendDatadogActionName: undefined,
  completeDatadogActionName: undefined,
};

export default InputCode;
